import { AlertCircle, Loader, PanelRightClose, PanelRightOpen } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useMinWidth } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

// Keep the device mounted when changing tools, so inspection never reconnects it.
export function DeviceWorkspace({
  title,
  status,
  actions,
  device,
  inspector,
  onInspectorVisibilityChange,
  message,
  error,
  errorAction,
}: {
  title: string;
  status: ReactNode;
  actions: ReactNode;
  device: ReactNode;
  inspector?: ReactNode;
  onInspectorVisibilityChange?: (visible: boolean) => void;
  message?: string | null;
  error?: string;
  errorAction?: ReactNode;
}) {
  const wide = useMinWidth(1024);
  const [inspectorOpen, setInspectorOpen] = useState<boolean>();
  const showInspector = inspectorOpen ?? wide;
  const hasInspector = !!inspector;
  useEffect(() => {
    onInspectorVisibilityChange?.(hasInspector && showInspector);
  }, [hasInspector, showInspector, onInspectorVisibilityChange]);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b pb-3">
        <div className="flex min-w-0 basis-full items-center gap-3 sm:min-w-40 sm:flex-1 sm:basis-auto">
          <h1 className="truncate text-sm font-semibold" title={title}>
            {title}
          </h1>
          <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
            {status}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <div className="flex flex-wrap items-center gap-1 [&>button]:px-2 sm:[&>button]:px-3">
            {actions}
          </div>
          {hasInspector && (
            <Button
              size="icon"
              className="size-8 shrink-0"
              title={showInspector ? "Hide inspector" : "Show inspector"}
              variant={showInspector ? "secondary" : "ghost"}
              aria-label={showInspector ? "Hide inspector" : "Show inspector"}
              aria-expanded={showInspector}
              aria-controls="device-inspector"
              onClick={() => setInspectorOpen(!showInspector)}
            >
              {showInspector ? <PanelRightClose /> : <PanelRightOpen />}
            </Button>
          )}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-0 lg:flex-row">
        <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden bg-muted/30 p-4 sm:p-6">
          <div className="h-full min-h-0" inert={!!message || !!error}>{device}</div>
          {(message || error) && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/40 p-4">
              <div role={error ? "alert" : "status"} className="flex max-h-full max-w-sm items-start gap-3 overflow-auto rounded-lg border bg-background p-4 shadow-sm">
                {error ? <AlertCircle aria-hidden className="mt-0.5 size-4 shrink-0 text-destructive" /> : <Loader aria-hidden className="mt-0.5 size-4 shrink-0 animate-spin text-muted-foreground" />}
                <div className="min-w-0 break-words text-sm">
                  <p>{error ?? message}</p>
                  {error && errorAction && <div className="mt-3">{errorAction}</div>}
                </div>
              </div>
            </div>
          )}
        </div>
        {hasInspector && (
          <aside
            id="device-inspector"
            aria-label="Device inspector"
            className={cn(
              "min-h-0 shrink-0 flex-col overflow-hidden border-t bg-background lg:border-l lg:border-t-0",
              showInspector ? "flex h-[50%] lg:h-full lg:w-96" : "hidden",
            )}
          >
            {inspector}
          </aside>
        )}
      </div>
    </div>
  );
}
